/* Image with Text – Custom: dynamic media sizing.
   CSS alone (aspect-ratio driven from a flex-stretched height) is
   unreliable here — the media column's height only becomes definite
   via stretch AFTER the browser has already tried to resolve how
   much width an aspect-ratio item needs, so it doesn't consistently
   resolve to the image's true shape in practice, and the image ends
   up cropped by object-fit:cover. This script sidesteps that timing
   issue entirely: it measures the text column's actual rendered
   height in pixels, then sets the media column's height AND width
   from that measurement directly.
   Runs only above the mobile breakpoint (750px).

   LOOP-SAFETY FIX (2026-07-27): sync() skips writing to the DOM when
   the newly measured height rounds to the same value already applied.

   SINGLE-EXECUTION GUARD (2026-07-30): a page using this section type
   more than once previously ran this whole IIFE once per instance. A
   single global flag now makes every execution after the first a no-op.

   SANITY-CLAMP FIX (2026-07-30): clamps the measured height to a sane
   band (120px–1600px) before ever writing it.

   STUCK-LAZY-LOAD FIX (2026-07-30, revised): the base <img> has native
   loading="lazy" and was confirmed, by direct testing, to get stuck at
   0×0 — img.complete stays false and no network request ever fires —
   even once the element is scrolled fully into view. Forcing
   loading='eager' + re-touching src always fixes it in under a second,
   proving the image URL is fine and only the browser's own lazy-load
   trigger is failing to fire.
   An IntersectionObserver-based watcher was tried first, but testing
   showed IntersectionObserver callbacks can themselves fail to fire at
   all in certain embedded/automated rendering contexts (confirmed even
   a fresh observer on document.body never called back) — exactly the
   kind of non-standard context (an embedded editor preview iframe) this
   bug was originally reported from. Relying on IntersectionObserver to
   fix an IntersectionObserver-adjacent bug is fragile, so the watchdog
   below instead polls plain getBoundingClientRect() on a fixed timer.
   setInterval doesn't depend on any render-tied observer callback at
   all, so it fires reliably in every context, including ones where
   native lazy-load or IntersectionObserver itself stalls. Stops once
   every tracked image has loaded (or after a generous cutoff) so it
   never runs forever.

   FORCED-REFLOW FIX (2026-08-03): both the media-sizing loop below and
   the lazy-load poll loop further down used to read a geometry property
   (getBoundingClientRect) and then immediately write to the DOM for EACH
   container/image inside the same forEach — writing one element's
   style/attributes invalidates the layout the browser just computed,
   forcing a fresh synchronous layout recalculation on the very next
   element's read ("layout thrashing"). Both loops are now split into a
   read pass (nothing written yet) followed by a write pass, so the
   browser only needs one layout pass per batch, not one per element.
   sync(container) itself is unchanged and still used as-is for the
   ResizeObserver callback below, since that path only ever measures one
   container at a time — there's nothing to batch there. */
(function () {
  if (window.__iwtCustomMediaSyncInit) return;
  window.__iwtCustomMediaSyncInit = true;

  var MIN_HEIGHT = 120;
  var MAX_HEIGHT = 1600;

  var lastHeights = new WeakMap();

  function measureContainer(container) {
    var content = container.querySelector('.iwt-custom__content');
    var media = container.querySelector('.iwt-custom__media');
    if (!content || !media) return null;

    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isMobile) {
      return { container: container, media: media, mobile: true };
    }

    var ratio = parseFloat(media.getAttribute('data-image-ratio')) || 1;
    var height = content.getBoundingClientRect().height;
    if (height <= 0) return null;

    height = Math.min(Math.max(height, MIN_HEIGHT), MAX_HEIGHT);
    var rounded = Math.round(height);

    return { container: container, media: media, mobile: false, height: height, rounded: rounded, ratio: ratio };
  }

  function applyMeasurement(measurement) {
    if (!measurement) return;

    if (measurement.mobile) {
      if (measurement.media.style.height !== '' || measurement.media.style.width !== '') {
        measurement.media.style.height = '';
        measurement.media.style.width = '';
        lastHeights.delete(measurement.container);
      }
      return;
    }

    if (lastHeights.get(measurement.container) === measurement.rounded) return;
    lastHeights.set(measurement.container, measurement.rounded);

    measurement.media.style.height = measurement.height + 'px';
    measurement.media.style.width = (measurement.height * measurement.ratio) + 'px';
  }

  function sync(container) {
    // Single-container path (used by the ResizeObserver callback below) —
    // no batching needed since only one element's geometry is read here.
    applyMeasurement(measureContainer(container));
  }

  function syncMany(containers) {
    // Batches ALL geometry reads (measureContainer) across every container
    // in the set BEFORE applying any DOM write (applyMeasurement). See the
    // FORCED-REFLOW FIX note at the top of this file.
    var measurements = [];
    containers.forEach(function (container) {
      measurements.push(measureContainer(container));
    });
    measurements.forEach(applyMeasurement);
  }

  var observedContainers = new WeakSet();

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var containers = scope.querySelectorAll('[data-dynamic-media]');

    syncMany(containers);

    containers.forEach(function (container) {
      var content = container.querySelector('.iwt-custom__content');
      if (!content) return;

      if (observedContainers.has(container)) return;
      observedContainers.add(container);

      if (window.ResizeObserver) {
        var observer = new ResizeObserver(function () {
          sync(container);
        });
        observer.observe(content);
      }
    });
  }

  function syncAll() {
    syncMany(document.querySelectorAll('[data-dynamic-media]'));
  }

  window.addEventListener('resize', syncAll);

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  window.addEventListener('load', function () {
    setTimeout(syncAll, 250);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // ---- Stuck-lazy-load watchdog (timer-based, not observer-based) ----
  var VIEWPORT_MARGIN = 800; // px — unstick a bit before it's actually visible
  var POLL_INTERVAL = 300;   // ms
  var MAX_POLLS = 100;       // ~30s ceiling, generous but not infinite
  var unstuckImages = new WeakSet();

  function forceLoad(img) {
    if (unstuckImages.has(img)) return;
    unstuckImages.add(img);
    img.loading = 'eager';
    var src = img.getAttribute('src');
    var srcset = img.getAttribute('srcset');
    if (src) img.setAttribute('src', src);
    if (srcset) img.setAttribute('srcset', srcset);
  }

  function pollLazyImages() {
    var imgs = document.querySelectorAll('.iwt-custom__media img[loading="lazy"]');
    var stillPending = false;
    var toForceLoad = [];

    // Read pass only — see the FORCED-REFLOW FIX note at the top of this
    // file for why writes are deferred to a second pass below.
    imgs.forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) return;
      stillPending = true;

      var rect = img.getBoundingClientRect();
      var nearViewport =
        rect.bottom > -VIEWPORT_MARGIN &&
        rect.top < window.innerHeight + VIEWPORT_MARGIN;

      if (nearViewport) toForceLoad.push(img);
    });

    // Write pass, now that every read above is already done.
    toForceLoad.forEach(forceLoad);

    return stillPending;
  }

  var pollCount = 0;
  var pollTimer = setInterval(function () {
    pollCount++;
    var stillPending = pollLazyImages();
    if (!stillPending || pollCount >= MAX_POLLS) {
      clearInterval(pollTimer);
    }
  }, POLL_INTERVAL);

  // Also check immediately on load/DOMContentLoaded rather than waiting
  // for the first interval tick.
  document.addEventListener('DOMContentLoaded', pollLazyImages);
  window.addEventListener('load', pollLazyImages);
})();
